<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
	modelValue: {
		type: String ,
		required: true,
		default: ''
	},
	list: {
		type: Array,
		required: true,
		default: () => []
	},
	keyField: {
		type: String,
		required: true,
		default: ''	
	},
	disableFields: {
		type: Array,
		required: false,
		default: () => []
	},
	shellClass: {
		type: String,
		default: ''
	},
	btnClass: {
		type: String,
		default: ''
	}	
})

const emit = defineEmits(['selectedDropdown'])

//reactive proxies
const isOpen = ref(false)
const focusedIndex = ref(-1)
const dropDownRef = ref(null)

//Methods
// const toggleDropdown = () => {
//   isOpen.value = !isOpen.value
// }

const selectOption = (option) => {
	emit('selectedDropdown', option[props.keyField])
	//emit('update:modelValue', option[props.keyField])
	isOpen.value = false
}

const selectedLabel = computed(() => {
	const selected = props.list.find((opt) => opt[props.keyField] === props.modelValue)
	return selected ? selected[props.keyField] : props.modelValue
})

const toggleDropdown = () => {
	isOpen.value = !isOpen.value
	if (isOpen.value) {
		focusSelectedOrFirst()
	}
}

const focusSelectedOrFirst = () => {
	const index = props.list.findIndex(opt => opt[props.keyField] === props.modelValue)
	focusedIndex.value = index !== -1 ? index : 0
}

// Handle outside click
const handleClickOutside = (event) => {
	if (dropDownRef.value && !dropDownRef.value.contains(event.target)) {
		isOpen.value = false
	}
}

const handleKeydown = (e) => {
	if (!isOpen.value || props.list.length === 0) return

	if (e.key === 'ArrowDown') {
		focusedIndex.value = (focusedIndex.value + 1) % props.list.length
		e.preventDefault()
	} else if (e.key === 'ArrowUp') {
		focusedIndex.value = (focusedIndex.value - 1 + props.list.length) % props.list.length
		e.preventDefault()
	} else if (e.key === 'Enter') {
		const option = props.list[focusedIndex.value]
		if (option) selectOption(option)
			e.preventDefault()
	} else if (e.key === 'Escape') {
		isOpen.value = false
		e.preventDefault()
	}
}

onMounted(() => {
	document.addEventListener('click', handleClickOutside)
  	document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside)
  	document.removeEventListener('keydown', handleKeydown)
})

//@keydown.down.prevent="openAndFocus(0)"
//@keydown.enter.prevent="toggleDropdown"
      	
</script>


<template>
<div class="dropdown" ref="dropDownRef" :class="props.shellClass">
    <button class="dropdown-button" :class="props.btnClass"
		@click="toggleDropdown" :aria-label="props.keyField"
		@keydown.space.prevent="toggleDropdown"
      	@keydown.esc.prevent="isOpen = false">
		<div>{{selectedLabel}}</div>
      	<div class="dropdown-icon"><i class="fa-solid fa-chevron-down"></i></div>
    </button>
    <ul v-if="isOpen" class="dropdown-menu" role="listbox" aria-label="Dropdown">
		<li v-for="(item, index) in props.list"
			:key="index"
			@click="selectOption(item)"
			@mouseenter="focusedIndex = index"
			class="dropdown-item"
			:class="{ focused: index === focusedIndex, disabled: disableFields.includes(item[props.keyField]) }"
			role="option" 
			:aria-selected="modelValue === item[props.keyField]">
        	{{ item[props.keyField] }}
      	</li>
    </ul>
</div>
</template>
   
<style scoped>
.dropdown {
	position: relative;
	min-width: 200px;
	height: 2.5rem;
	display:flex; 
	justify-content: space-between;
}

.dropdown-button {
	width: 100%;
	padding: 4px 12px;
	height: 2.5rem;
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-weight: 500;
	background-color: white;
	color: #333;
	border-radius: .25rem;
	border: 1px solid var(--border-color-input-Border, #A9B7C7);
	background: var(--input-background-Default, #FFF);

	cursor: pointer;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.dropdown-button:active, .dropdown-button:focus {
    outline-color: var(--border-color-input-Active);
    outline-width: 1.5px;
    outline-style: solid;
    outline-offset: 0;	
}

.dropdown-button:hover {
	background-color: #f9f9f9;
	text-decoration: none;
}

.dropdown-icon {
	font-size: 12px;
	margin-left: 8px;
	color: var(--button-color-primary-Default); 
}

.dropdown-menu {
	position: absolute;
	top: 2.5rem;;
	left: 0;
	margin-top: 0;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	z-index: 1000;
	list-style: none;
	padding: 0;
    text-wrap: nowrap;
    /* max-width: 500px; */	
	min-width: 100%;
	border-radius: 0.25rem;
	border: 1px solid var(--border-color-input-Border, #A9B7C7);
	background-color: var(--input-background-hover, #FAFAFA);
	overflow: hidden;
	color: var(--text-Primary, #2C2C2C);
	text-overflow: ellipsis;
}

.dropdown-item {
	padding: 8px 12px;
	font-size: 14px;
	color: #333;
	cursor: pointer;
  	height: 2.5rem;
	padding: 0.5rem 0.75rem;
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem);
	background: var(--surfaces-surface-Default, #FFF);
	min-width: 200px;
}

.dropdown-item:hover {
	background-color: var(--input-background-Hover, #FAFAFA);
	border-top: 1.5px solid var(--borders-divider-Active, #064291);	
	border-bottom: 1.5px solid var(--borders-divider-Active, #064291);	
}

.dropdown-item:focus, .dropdown-item.focused {
	background-color: var(--input-background-Hover, #FAFAFA);
	border-top: 1.5px solid var(--borders-divider-Active, #064291);	
	border-bottom: 1.5px solid var(--borders-divider-Active, #064291);
}

.dropdown-item.disabled {
	pointer-events: none;
	cursor: not-allowed;
	color: var(--text-Disabled, #6C757D);
}

.dropdown-no-border {
	border: 0; /* 1px solid #ccc; */
}

.dropdown-100-width {
	width: 100%
}

button:focus {
	outline: 0;
}

/* 499px - Mobile */
@media (max-width: 31.1875rem) {
	.dropdown-no-border {
		text-align: justify;
		padding-left: 0;
	}
}
</style>
